<script lang="ts">
  // Search box + suggestions listbox (spec §14): saved searches (≤3), recent (until 6), then
  // up to 3 sender suggestions as `from:<email>`, max 8. Arrows cycle, Enter applies, Escape
  // closes; the dropdown closes 160 ms after blur so a click on an item lands first.
  import { t } from '$lib/i18n';
  import { store } from '$lib/store';
  import Icon from '../ui/Icon.svelte';

  interface Sug {
    kind: 'saved' | 'recent' | 'from';
    q: string;
    label: string;
    hint?: string;
  }

  let input = $state<HTMLInputElement | null>(null);
  let open = $state(false);
  let idx = $state(-1);
  let senders = $state<{ name: string; email: string }[]>([]);
  let closeTimer: ReturnType<typeof setTimeout> | undefined;

  const q = $derived(store.searchQ);

  const suggestions = $derived.by<Sug[]>(() => {
    const needle = q.trim().toLowerCase();
    const out: Sug[] = [];
    const saved = store.savedSearches.filter((s) => !needle || s.name.toLowerCase().includes(needle) || s.q.toLowerCase().includes(needle)).slice(0, 3);
    for (const s of saved) out.push({ kind: 'saved', q: s.q, label: s.name, hint: s.q });
    for (const r of store.recentSearches) {
      if (out.length >= 6) break;
      if (needle && !r.toLowerCase().includes(needle)) continue;
      if (out.some((x) => x.q === r)) continue;
      out.push({ kind: 'recent', q: r, label: r });
    }
    for (const s of senders.slice(0, 3)) {
      if (out.length >= 8) break;
      const sq = `from:${s.email}`;
      if (out.some((x) => x.q === sq)) continue;
      out.push({ kind: 'from', q: sq, label: s.name || s.email, hint: s.name ? s.email : undefined });
    }
    return out.slice(0, 8);
  });

  const listOpen = $derived(open && suggestions.length > 0);

  // Sender suggestions for ≥ 2 characters (top 3), dropped when the query moves on.
  $effect(() => {
    const needle = q.trim();
    if (needle.length < 2) {
      senders = [];
      return;
    }
    let live = true;
    void store.suggest(needle, 3).then((r) => {
      if (live) senders = r;
    });
    return () => {
      live = false;
    };
  });

  export function focus(): void {
    input?.focus();
  }
  export function element(): HTMLInputElement | null {
    return input;
  }

  function onInput(ev: Event) {
    store.setSearch((ev.currentTarget as HTMLInputElement).value);
    open = true;
    idx = -1;
  }

  function apply(s: Sug) {
    store.setSearch(s.q);
    store.rememberSearch(s.q);
    open = false;
    idx = -1;
  }

  function onKey(ev: KeyboardEvent) {
    if (ev.key === 'ArrowDown' && suggestions.length) {
      ev.preventDefault();
      open = true;
      idx = (idx + 1) % suggestions.length;
      return;
    }
    if (ev.key === 'ArrowUp' && suggestions.length) {
      ev.preventDefault();
      open = true;
      idx = (idx - 1 + suggestions.length) % suggestions.length;
      return;
    }
    if (ev.key === 'Enter') {
      ev.preventDefault();
      const s = listOpen && idx >= 0 ? suggestions[idx] : undefined;
      if (s) apply(s);
      else {
        store.rememberSearch(q);
        open = false;
      }
      return;
    }
    if (ev.key === 'Escape' && listOpen) {
      ev.preventDefault();
      ev.stopPropagation();
      open = false;
      idx = -1;
    }
  }

  function onFocus() {
    clearTimeout(closeTimer);
    open = true;
  }
  function onBlur() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      open = false;
      idx = -1;
    }, 160);
  }
  function clear() {
    store.clearSearch();
    input?.focus();
  }
</script>

<div class="search" class:active={!!q}>
  <input
    role="combobox"
    aria-controls="tik-sug"
    aria-autocomplete="list"
    aria-expanded={listOpen}
    aria-activedescendant={listOpen && idx >= 0 ? `tik-sug-${idx}` : undefined}
    aria-label={t('searchHint')}
    placeholder={t('searchHint')}
    autocomplete="off"
    spellcheck="false"
    value={q}
    bind:this={input}
    oninput={onInput}
    onkeydown={onKey}
    onfocus={onFocus}
    onblur={onBlur}
  />
  {#if q}
    <button type="button" class="ghost" aria-label={t('back')} onclick={clear}><Icon name="x" size={16} /></button>
  {/if}
  {#if listOpen}
    <div class="sug" id="tik-sug" role="listbox">
      {#each suggestions as s, i (s.kind + ':' + s.q)}
        <button type="button" role="option" id="tik-sug-{i}" class="sug-item" class:on={i === idx} aria-selected={i === idx} data-kind={s.kind} onpointerdown={(ev) => ev.preventDefault()} onclick={() => apply(s)}>
          <Icon name={s.kind === 'saved' ? 'sort' : s.kind === 'recent' ? 'refresh' : 'users'} size={15} />
          <span class="sug-label"><bdi>{s.label}</bdi></span>
          {#if s.hint}<span class="sug-hint" dir="ltr">{s.hint}</span>{/if}
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  /* @css shell/SearchBox start */
  input {
    block-size: var(--control-lg);
    padding: 0 var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    background: var(--surface-card);
    color: var(--text-body);
    width: 100%;
    transition: border-color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)
  }

  input:focus {
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus);
    outline: none
  }

  .sug {
    position: absolute;
    top: calc(100% + var(--space-1));
    inset-inline-start: 0;
    inline-size: 100%;
    z-index: var(--z-layer);
    display: grid;
    padding: var(--space-1);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-card);
    box-shadow: var(--shadow-panel);
    max-block-size: min(340px, 60dvh);
    overflow-y: auto
  }

  .sug-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-block-size: var(--control-md);
    padding-inline: var(--space-3);
    border-radius: var(--radius-control);
    color: var(--text-body);
    text-align: start
  }

  .sug-item:hover,
  .sug-item.on {
    background: var(--surface-sunken)
  }

  .sug-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
  }

  .sug-hint {
    flex: none;
    max-width: 45%;
    color: var(--text-muted);
    font-size: var(--text-xs);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
  }

  .search {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    gap: var(--space-1);
    background: var(--surface-sunken);
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    padding-inline: var(--space-3);
    height: 40px;
    max-width: 520px;
    transition: background var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard)
  }

  .search:focus-within {
    background: var(--surface-card);
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus)
  }

  .search input {
    flex: 1;
    width: 0;
    border: none;
    background: none;
    outline: none;
    padding: 0;
    color: var(--text-body);
    height: 100%;
    min-width: 0
  }

  .ghost {
    padding: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted)
  }

  .ghost:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .ghost:active {
    background: var(--mail-pressed)
  }

  @media (max-width:480px) {
  :global(.top) .search {
      flex: 1 1 100%;
      order: -1;
      max-width: none
    }
  }
  /* @css shell/SearchBox end */
</style>
